import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Pencil, Settings, LogOut, ChevronDown, ChevronUp, Copy, Users, Gift } from 'lucide-react';
import { queryKeys } from '@/api/queryKeys';
import { get } from '@/api/client';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Avatar } from '@/components/atoms/Avatar';
import { Button } from '@/components/atoms/Button';
import { CheckinCard } from '@/components/organisms/CheckinCard';
import { LevelInfoModal } from '@/components/organisms/LevelInfoModal';
import { ExpandableText } from '@/components/atoms/ExpandableText';
import { progressToNextLevel, LEVEL_INFO } from '@/constants/levels';
import { useAuthStore } from '@/store/authStore';
import { confirmModal } from '@/store/confirmStore';
import { comma } from '@/lib/format';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import { SEOHead } from '@/components/atoms/SEOHead';

interface MeData {
  user_id: string;
  nickname: string;
  bio: string | null;
  profile_image_url: string | null;
  level: number;
  score: number;
  activity_score: number;
  point_balance: number;
  post_count: number;
  comment_count: number;
  follower_count: number;
  following_count: number;
  diary_count: number;
  is_premium: boolean;
  interest_tags: string[] | null;
  invest_start_year: number | null;
  invest_years: number | null;
  invest_years_domestic: number | null;
  invest_years_usa: number | null;
  invest_years_coin: number | null;
  invest_years_etc: number | null;
  /** 소셜 프로바이더 — 안내 문구에서 가입 경로별 분기에 사용 */
  social_provider: string | null;
  /** DB email 컬럼 존재 여부 (실제 값은 노출하지 않음) */
  has_email: boolean;
}

const INTEREST_LABEL: Record<string, { label: string }> = {
  coin: { label: '코인' },
  stock: { label: '국내주식' },
  global: { label: '해외주식' },
  realestate: { label: '부동산' },
  macro: { label: '거시경제' },
  'ai-invest': { label: 'AI·퀀트' },
};

function formatInvestYears(years: number | null | undefined) {
  if (!years || years < 1) return null;
  if (years >= 10) return '10년 이상';
  return `${years}년차`;
}

/** 4개 분야 투자 기간 라벨 */
const INVEST_CATEGORIES: Array<{
  key: 'invest_years_domestic' | 'invest_years_usa' | 'invest_years_coin' | 'invest_years_etc';
  label: string;
}> = [
  { key: 'invest_years_domestic', label: '국내주식' },
  { key: 'invest_years_usa', label: '미국주식' },
  { key: 'invest_years_coin', label: '코인' },
  { key: 'invest_years_etc', label: '기타' },
];

export function MyPage() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [levelOpen, setLevelOpen] = useState(false);
  const { data } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: () => get<MeData>('/users/me'),
  });

  interface FavoriteBoard {
    id: number;
    slug: string;
    name: string;
    description: string | null;
  }
  const favoritesQuery = useQuery({
    queryKey: ['boards', 'favorites', 'me'],
    queryFn: () => get<{ favorites: FavoriteBoard[] }>('/boards/favorites'),
    staleTime: 60_000,
  });
  const favoriteBoards = favoritesQuery.data?.favorites ?? [];

  /* ── 관심종목(ticker_subscriptions) — 대량 대비 접기/펼치기 ───
     초기 노출 8개, 그 이상은 "더보기" 토글로 확장. 50+ 도 카드 단순 구성이라 브라우저 렌더링 부담 낮음. */
  interface SubscriptionRow {
    id: number;
    ticker: string;
    asset_type: 'stock' | 'coin' | 'us_stock';
    threshold: number;
    created_at: string;
    /** ticker_map 이 있는 종목만 채워짐. 코인은 null → COIN_NAMES_KR 로 폴백 */
    name: string | null;
    name_en: string | null;
    market: string | null;
  }
  const subsQuery = useQuery({
    queryKey: ['subscriptions', 'me'],
    queryFn: () => get<{ list: SubscriptionRow[] }>('/subscriptions'),
    staleTime: 60_000,
  });
  const subscriptions = subsQuery.data?.list ?? [];
  const [subsExpanded, setSubsExpanded] = useState(false);
  const SUBS_PREVIEW = 8;
  const visibleSubs = subsExpanded ? subscriptions : subscriptions.slice(0, SUBS_PREVIEW);
  const hiddenCount = Math.max(0, subscriptions.length - SUBS_PREVIEW);

  /** 종목 표시명 — 주식은 ticker_map.name, 코인은 COIN_NAMES_KR 매핑, 없으면 ticker 그대로 */
  const subDisplayName = (s: SubscriptionRow) => {
    if (s.ticker.startsWith('KRW-')) {
      return COIN_NAMES_KR[s.ticker] ?? stripKrw(s.ticker);
    }
    return s.name ?? s.ticker;
  };

  /** asset_type → 칩 라벨 */
  const ASSET_LABEL: Record<SubscriptionRow['asset_type'], string> = {
    stock: '국내',
    us_stock: '해외',
    coin: '코인',
  };

  const handleLogout = async () => {
    const ok = await confirmModal({
      title: '로그아웃',
      message: '정말 로그아웃 하시겠어요?',
      confirmLabel: '로그아웃',
    });
    if (!ok) return;
    navigate('/', { replace: true });
    logout();
  };

  if (!data) return null;

  const score = data.activity_score ?? data.score ?? 0;
  const { current, next, percent, remaining } = progressToNextLevel(score);
  const investChips = INVEST_CATEGORIES.map((c) => ({
    label: c.label,
    years: formatInvestYears(data[c.key]),
  })).filter((c) => !!c.years);

  return (
    <div className="container-base py-6">
      <SEOHead
        title="마이페이지"
        description="시세공간 마이페이지 — 활동 점수, 포인트, 매매일지, 팔로우 관리"
        canonical="/me"
      />
      {/* 프로필 카드 (상단) */}
      <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        {/* 우측 상단 아이콘 버튼 */}
        <div className="absolute" />
        <div className="relative">
          <div className="absolute right-0 top-0 flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/me/edit')}
              aria-label="프로필 수정"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--color-border)] bg-white text-gray-600 hover:bg-gray-50"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/settings')}
              aria-label="설정/알림"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--color-border)] bg-white text-gray-600 hover:bg-gray-50"
            >
              <Settings className="h-4 w-4" />
            </button>
          </div>

          {/* 아바타(좌) + 닉네임/레벨/bio(우) 한 행 구조 */}
          <div className="flex items-start gap-4 pr-24">
            <Avatar
              src={data.profile_image_url ?? undefined}
              alt={data.nickname}
              size="lg"
              className="shrink-0"
            />
            <div className="min-w-0 flex-1 pt-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-xl font-bold">{data.nickname}</h2>
                <LevelBadge level={data.level} onClick={() => setLevelOpen(true)} />
                {data.is_premium && (
                  <span className="rounded bg-gradient-to-r from-amber-400 to-orange-500 px-2 py-0.5 text-xs font-bold text-white">
                    PREMIUM
                  </span>
                )}
              </div>
              {data.bio ? (
                <div className="mt-1">
                  <ExpandableText
                    text={data.bio}
                    lines={2}
                    className="text-sm text-[var(--color-text-sub)]"
                  />
                </div>
              ) : (
                <p className="mt-1 text-sm italic text-[var(--color-text-sub)]/60">
                  한줄소개를 입력해 보세요
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 관심분야 */}
        {!!data.interest_tags?.length && (
          <div className="mt-4">
            <div className="mb-1.5 text-xs text-[var(--color-text-sub)]">관심분야</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {data.interest_tags.map((tag) => {
                const meta = INTEREST_LABEL[tag];
                if (!meta) return null;
                return (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-primary)]"
                  >
                    {meta.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* 투자기간 (4개 분야별) */}
        {investChips.length > 0 && (
          <div className="mt-3">
            <div className="mb-1.5 text-xs text-[var(--color-text-sub)]">투자기간</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {investChips.map((c) => (
                <span
                  key={c.label}
                  className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1 text-xs font-medium"
                >
                  {c.label} {c.years}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 레벨 진행 */}
        <div className="mt-6">
          <div className="mb-1 flex justify-between text-xs">
            <button
              type="button"
              onClick={() => setLevelOpen(true)}
              className="hover:underline"
            >
              현재 {LEVEL_INFO[current].label} · {comma(score)}점
            </button>
            {next ? (
              <span className="text-[var(--color-text-sub)]">
                다음 {LEVEL_INFO[next].label}까지 {comma(remaining)}점
              </span>
            ) : (
              <span>최고 레벨 달성!</span>
            )}
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
            <div
              className="h-full rounded-full bg-[var(--color-primary)] transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        {/* 통계 (6등분): 포인트 / 게시글 / 매매일지 / 댓글 / 팔로워 / 팔로잉
            모바일(375px)에선 6등분 시 각 ~58px 로 숫자·라벨이 잘림 → sm 이하는 3등분 2줄 */}
        <div className="mt-6 grid grid-cols-3 gap-1 border-t border-[var(--color-border)] pt-4 text-center sm:grid-cols-6">
          <Link to="/points/history" className="rounded-lg py-2 hover:bg-[var(--color-bg)]">
            <div className="text-base font-bold">{comma(data.point_balance)}</div>
            <div className="text-xs text-[var(--color-text-sub)]">포인트</div>
          </Link>
          <Link
            to={`/users/${data.user_id}/posts`}
            className="rounded-lg py-2 hover:bg-[var(--color-bg)]"
          >
            <div className="text-base font-bold">{data.post_count}</div>
            <div className="text-xs text-[var(--color-text-sub)]">게시글</div>
          </Link>
          <Link to="/diary" className="rounded-lg py-2 hover:bg-[var(--color-bg)]">
            <div className="text-base font-bold">{data.diary_count ?? 0}</div>
            <div className="text-xs text-[var(--color-text-sub)]">매매일지</div>
          </Link>
          <Link to="/profile/me/comments" className="rounded-lg py-2 hover:bg-[var(--color-bg)]">
            <div className="text-base font-bold">{data.comment_count ?? 0}</div>
            <div className="text-xs text-[var(--color-text-sub)]">댓글</div>
          </Link>
          <Link
            to={`/users/${data.user_id}/followers`}
            className="rounded-lg py-2 hover:bg-[var(--color-bg)]"
          >
            <div className="text-base font-bold">{data.follower_count}</div>
            <div className="text-xs text-[var(--color-text-sub)]">팔로워</div>
          </Link>
          <Link
            to={`/users/${data.user_id}/following`}
            className="rounded-lg py-2 hover:bg-[var(--color-bg)]"
          >
            <div className="text-base font-bold">{data.following_count}</div>
            <div className="text-xs text-[var(--color-text-sub)]">팔로잉</div>
          </Link>
        </div>
      </section>

      {/* 관심게시판 — 등록한 게시판 바로가기 */}
      <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="section-title">관심게시판</h2>
          <span className="text-xs text-[var(--color-text-sub)]">
            {favoriteBoards.length}개
          </span>
        </div>
        {favoritesQuery.isLoading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-[var(--color-bg)]" />
            ))}
          </div>
        ) : favoriteBoards.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {favoriteBoards.map((b) => (
              <Link
                key={b.id}
                to={`/board/${b.slug}`}
                className="rounded-lg border border-[var(--color-border)] bg-white px-3 py-2.5 text-sm font-medium transition-colors hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/5"
              >
                <div className="truncate">{b.name}</div>
                {b.description && (
                  <div className="mt-0.5 truncate text-xs text-[var(--color-text-sub)]">
                    {b.description}
                  </div>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-[var(--color-text-sub)]">
            관심게시판이 없어요. 각 게시판 상단 버튼으로 등록해보세요.
          </p>
        )}
      </section>

      {/* 관심종목 — TickerPage 에서 등록한 종목 리스트
          대량 등록 시 최초 8개만 노출하고 나머지는 "더보기" 토글 */}
      <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="section-title">관심종목</h2>
          <span className="text-xs text-[var(--color-text-sub)]">
            {subscriptions.length}개
          </span>
        </div>
        {subsQuery.isLoading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-[var(--color-bg)]" />
            ))}
          </div>
        ) : subscriptions.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {visibleSubs.map((s) => (
                <Link
                  key={s.id}
                  to={`/ticker/${s.ticker}`}
                  className="group flex flex-col gap-1 rounded-lg border border-[var(--color-border)] bg-white px-3 py-2.5 text-sm transition-colors hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/5"
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="truncate font-semibold">{subDisplayName(s)}</span>
                    <span className="shrink-0 rounded bg-[var(--color-bg)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-text-sub)]">
                      {ASSET_LABEL[s.asset_type] ?? s.asset_type}
                    </span>
                  </div>
                  <div className="truncate text-xs text-[var(--color-text-sub)]">
                    {s.ticker}
                  </div>
                </Link>
              ))}
            </div>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setSubsExpanded((v) => !v)}
                className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-[var(--color-border)] bg-white py-2 text-sm font-medium text-[var(--color-text-sub)] transition-colors hover:bg-[var(--color-bg)]"
              >
                {subsExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    접기
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    더보기 ({hiddenCount}개)
                  </>
                )}
              </button>
            )}
          </>
        ) : (
          <p className="py-4 text-center text-sm text-[var(--color-text-sub)]">
            관심종목이 없어요. 종목 페이지에서 "관심종목 추가" 버튼으로 등록해보세요.
          </p>
        )}
      </section>

      {/* 이메일 미등록 안내 — 이메일이 없으면 미접속 사전 안내 메일 등 발송이 불가하므로 알림 */}
      {data && data.has_email === false && <EmailMissingNotice provider={data.social_provider} />}

      {/* 친구 초대 — 추천 링크 + 친구 수 + 누적 포인트 */}
      <ReferralSection />

      {/* 출석체크 캘린더 (하단) */}
      <CheckinCard />

      {/* 액션 (프로필 수정·설정은 카드 우측 상단으로 이동) */}
      <section className="space-y-2">
        <Button fullWidth variant="secondary" onClick={() => navigate('/exchange')}>
          교환소
        </Button>
        <Button fullWidth variant="secondary" onClick={() => navigate('/exchange/history')}>
          교환 내역
        </Button>
        <Button fullWidth variant="ghost" onClick={handleLogout}>
          <LogOut className="h-4 w-4" />
          로그아웃
        </Button>
      </section>

      <LevelInfoModal
        open={levelOpen}
        onClose={() => setLevelOpen(false)}
        currentLevel={data.level}
        activityScore={score}
      />
    </div>
  );
}

/**
 * 이메일 미등록 안내 — 소셜 동의 시 이메일 항목을 거부했거나 비즈앱 미설정 등으로
 * DB 의 email 컬럼이 NULL 인 사용자에게 안내. 카카오 사용자가 가장 빈번.
 * 재로그인 시 userService.findOrCreateBySocial 의 백필 로직이 자동으로 email 을 채움.
 */
function EmailMissingNotice({ provider }: { provider: string | null }) {
  const isKakao = provider === 'kakao';
  const providerLabel = isKakao ? '카카오' : provider === 'naver' ? '네이버' : provider === 'google' ? '구글' : '소셜';
  return (
    <section
      aria-label="이메일 미등록 안내"
      className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
    >
      <p className="font-semibold">이메일 정보가 없습니다.</p>
      <p className="mt-1 text-xs leading-relaxed text-amber-800">
        {providerLabel} 재로그인 시 자동으로 업데이트됩니다.
        {isKakao && ' (카카오 동의 화면에서 "이메일" 항목에 동의해주세요)'}
      </p>
      <p className="mt-2 text-[11px] leading-relaxed text-amber-700">
        이메일이 등록되지 않으면 장기 미접속 사전 안내, 교환 처리 알림 등 주요 메일이 발송되지 않아요.
      </p>
    </section>
  );
}

/**
 * 친구 초대 섹션 — 마이페이지 하단.
 * - 내 추천 링크 (https://{host}?ref={code}) + 복사 버튼
 * - 추천한 친구 수 + 누적 포인트 (100P × 친구수)
 * - GET /referrals/me 가 referral_code 와 합계 반환
 */
function ReferralSection() {
  const { data } = useQuery({
    queryKey: ['referrals', 'me'],
    queryFn: () => get<{ code: string | null; friendCount: number; earnedPoints: number }>('/referrals/me'),
    staleTime: 60_000,
  });
  const [copied, setCopied] = useState(false);
  if (!data || !data.code) return null;

  // 추천 링크 — production 기준 도메인 사용. 임시로 location.origin 으로 폴백.
  const link = `${window.location.origin}/?ref=${data.code}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* 비-https 환경 등은 무시 */
    }
  };

  return (
    <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
      <div className="mb-3 flex items-center gap-2">
        <Gift className="h-5 w-5 text-[var(--color-primary)]" />
        <h2 className="section-title">친구 초대</h2>
      </div>
      <p className="mb-4 text-xs text-[var(--color-text-sub)]">
        친구가 추천 링크로 가입하면 친구는 <strong className="text-[var(--color-text)]">50P</strong>,
        나는 <strong className="text-[var(--color-text)]">100P</strong> 받아요.
      </p>

      {/* 링크 + 복사 */}
      <div className="mb-4 flex items-center gap-2">
        <input
          type="text"
          readOnly
          value={link}
          className="min-w-0 flex-1 truncate rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-xs text-[var(--color-text-sub)]"
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button size="sm" onClick={onCopy}>
          <Copy className="h-3.5 w-3.5" />
          {copied ? '복사됨' : '복사'}
        </Button>
      </div>

      {/* 통계 — 친구 수 / 누적 포인트 2분할 */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-3 text-center">
          <div className="mb-0.5 flex items-center justify-center gap-1 text-[11px] text-[var(--color-text-sub)]">
            <Users className="h-3 w-3" />
            추천한 친구
          </div>
          <div className="text-xl font-extrabold tabular-nums">{data.friendCount}명</div>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-3 text-center">
          <div className="mb-0.5 flex items-center justify-center gap-1 text-[11px] text-[var(--color-text-sub)]">
            <Gift className="h-3 w-3" />
            받은 포인트
          </div>
          <div className="text-xl font-extrabold tabular-nums text-[var(--color-primary)]">
            {comma(data.earnedPoints)}P
          </div>
        </div>
      </div>
    </section>
  );
}
